import React, { createContext, useContext, useState, useCallback, useEffect } from 'react';
import {
  apiGetReports,
  apiSubmitTimesheet,
  apiSubmitOvertime,
  apiUpdateOvertime,
  apiDeleteOvertime,
  apiSubmitOvertimeEarly
} from '../utils/api';
import { getAuthToken } from '../utils/auth';
import { getMonthName, getWorkingDaysInMonth } from '../utils/dateHelpers';

const ReportsContext = createContext();
export const useReports = () => useContext(ReportsContext);

export const ReportsProvider = ({ children }) => {
  const [submittedReports, setSubmittedReports] = useState([]);
  const [timesheetData, setTimesheetData] = useState([{ project: '', days: '' }]);
  const [overtimeData, setOvertimeData] = useState({
    date: new Date().toISOString().split('T')[0],
    startTime: '09:00',
    endTime: '18:00',
    project: '',
    manager: '', // НОВОЕ: поле руководителя проекта
    workType: 'Переработка'
  });
  const [expandedTimesheetMonths, setExpandedTimesheetMonths] = useState([]);
  const [expandedOvertimeMonths, setExpandedOvertimeMonths] = useState([]);
  const [timesheetFilters, setTimesheetFilters] = useState({ project: '', month: '' });
  const [overtimeFilters, setOvertimeFilters] = useState({ project: '', workType: '', dateFrom: '', dateTo: '' });

  const loadUserReports = useCallback(async (token = null) => {
    const authToken = token || getAuthToken();
    if (!authToken) return;
    try {
      const data = await apiGetReports(authToken);
      if (data.success) {
        const formattedTimesheet = (data.timesheet || []).map(r => ({
          ...r,
          type: 'timesheet',
          user_id: r.user_id || r.userId,
          userName: r.userName || r.user_name || r.created_by || r.username || 'Пользователь',
          submittedAt: r.submitted_at || r.submittedAt || r.created_at,
          days: r.days,
          percentage: r.percentage,
          project: r.project,
          month: r.month || getMonthName(r.submitted_at || r.created_at),
        }));

        const formattedOvertime = (data.overtime || []).map(r => ({
          ...r,
          type: 'overtime',
          user_id: r.user_id || r.userId,
          userName: r.userName || r.user_name || r.created_by || r.username || 'Пользователь',
          submittedAt: r.submitted_at || r.submittedAt || r.created_at,
          startTime: r.start_time || r.startTime,
          endTime: r.end_time || r.endTime,
          workType: r.work_type || r.workType || 'Не указан',
          overtimeHours: r.overtime_hours || r.overtimeHours || 0,
          totalHours: r.total_hours || r.totalHours || 0,
          project: r.project,
          manager: r.manager || '', // НОВОЕ: руководитель проекта
          date: r.date,
          month: r.month || getMonthName(r.date),
          submittedEarly: r.submitted_early === 1 || r.submittedEarly === true // Нормализация флага
        }));

        setSubmittedReports([...formattedTimesheet, ...formattedOvertime]);
        console.log(`Загружено отчетов: ${formattedTimesheet.length + formattedOvertime.length}`);
      }
    } catch (error) {
      console.error('Ошибка загрузки отчетов:', error);
    }
  }, []);

  useEffect(() => {
    const token = getAuthToken();
    if (token) loadUserReports(token);
  }, [loadUserReports]);

  const submitTimesheet = useCallback(async (entries, userName) => {
    const workingDays = getWorkingDaysInMonth(new Date());
    const reports = entries.map(entry => ({
      userName,
      project: entry.project,
      days: entry.days,
      percentage: (parseFloat(entry.days) / workingDays * 100).toFixed(1),
      month: getMonthName(new Date())
    }));
    const token = getAuthToken();
    const data = await apiSubmitTimesheet(token, reports);
    if (data.success) {
      await loadUserReports(token);
      return { success: true, reports };
    }
    return { success: false, error: data.error };
  }, [loadUserReports]);

  const submitOvertime = useCallback(async (report, userName) => {
    const token = getAuthToken();
    const data = await apiSubmitOvertime(token, report);
    if (data.success) {
      await loadUserReports(token);
      return { success: true, report };
    }
    return { success: false, error: data.error };
  }, [loadUserReports]);

  const editOvertimeReport = useCallback(async (reportId, updatedReport) => {
    const token = getAuthToken();
    try {
      const data = await apiUpdateOvertime(token, reportId, updatedReport);
      if (data.success) {
        await loadUserReports(token);
        return { success: true };
      }
      return { success: false, error: data.error };
    } catch (error) {
      return { success: false, error: error.message };
    }
  }, [loadUserReports]);

  const deleteOvertimeReport = useCallback(async (reportId) => {
    const token = getAuthToken();
    try {
      const data = await apiDeleteOvertime(token, reportId);
      if (data.success) {
        setSubmittedReports(prev => prev.filter(r => r.id !== reportId));
        return { success: true };
      }
      return { success: false, error: data.error };
    } catch (error) {
      return { success: false, error: error.message };
    }
  }, []);

  const submitOvertimeEarly = useCallback(async (reportId) => {
    const token = getAuthToken();
    try {
      const data = await apiSubmitOvertimeEarly(token, reportId);
      if (data.success) {
        setSubmittedReports(prev =>
          prev.map(r => r.id === reportId ? { ...r, submittedEarly: true } : r)
        );
        return { success: true };
      }
      return { success: false, error: data.error };
    } catch (error) {
      return { success: false, error: error.message };
    }
  }, []);

  const groupReportsByMonth = (reports) =>
    reports.reduce((acc, report) => {
      const month = report.month || getMonthName(report.submittedAt || report.date || report.created_at);
      if (!acc[month]) acc[month] = [];
      acc[month].push(report);
      return acc;
    }, {});

  const filterTimesheetReports = (reports) =>
    reports.filter(report => {
      if (timesheetFilters.project && report.project !== timesheetFilters.project) return false;
      if (timesheetFilters.month && getMonthName(report.submittedAt) !== timesheetFilters.month) return false;
      return true;
    });

  const filterOvertimeReports = (reports) =>
    reports.filter(report => {
      if (overtimeFilters.project && report.project !== overtimeFilters.project) return false;
      if (overtimeFilters.workType && report.workType !== overtimeFilters.workType) return false;
      if (overtimeFilters.dateFrom && new Date(report.date) < new Date(overtimeFilters.dateFrom)) return false;
      if (overtimeFilters.dateTo && new Date(report.date) > new Date(overtimeFilters.dateTo)) return false;
      return true;
    });

  const calculateMonthTimesheetTotal = (reports) =>
    reports.reduce((sum, r) => sum + parseFloat(r.percentage || 0), 0).toFixed(1);

  const calculateMonthOvertimeTotal = (reports) =>
    reports.reduce((sum, r) => sum + (r.overtimeHours || 0), 0).toFixed(1);

  const toggleTimesheetMonth = (month) =>
    setExpandedTimesheetMonths(prev =>
      prev.includes(month) ? prev.filter(m => m !== month) : [...prev, month]
    );

  const toggleOvertimeMonth = (month) =>
    setExpandedOvertimeMonths(prev =>
      prev.includes(month) ? prev.filter(m => m !== month) : [...prev, month]
    );

  return (
    <ReportsContext.Provider value={{
      submittedReports,
      timesheetData, setTimesheetData,
      overtimeData, setOvertimeData,
      expandedTimesheetMonths, setExpandedTimesheetMonths,
      expandedOvertimeMonths, setExpandedOvertimeMonths,
      timesheetFilters, setTimesheetFilters,
      overtimeFilters, setOvertimeFilters,
      loadUserReports,
      submitTimesheet,
      submitOvertime,
      editOvertimeReport,
      deleteOvertimeReport,
      submitOvertimeEarly,
      groupReportsByMonth,
      filterTimesheetReports,
      filterOvertimeReports,
      calculateMonthTimesheetTotal,
      calculateMonthOvertimeTotal,
      toggleTimesheetMonth,
      toggleOvertimeMonth,
    }}>
      {children}
    </ReportsContext.Provider>
  );
};